import _ from "lodash";
import React, { ChangeEvent, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    CreateChartState,
    useCreateDashboardStore,
} from ".";
import CreateChartModal from "./modal/CreateChart";
import DashboardCreateSelectGatewayModal from "./modal/SelectGateways";
import { WidgetItem, Registry } from "types";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import {
    getChartSourceFromWidgetType,
    getWidgetConfigByWidgetType,
    WidgetType,
} from "../utils/widgetConfig";

/** @deprecated */
export enum GatewayChartFor {
    Widget = "Widget",
}

/** @deprecated */
export enum GateWayChartType {
    Widget = "Widget Chart",
}

type FormGateway = {
    gatewayId: string;
    gatewayName: string;
    chartName: string;
    uuid: string;
    deviceId: string;
    showLocation?: boolean;
    hasLocation?: boolean;
};

type FormData = {
    chartName?: string;
    gateways?: FormGateway[];
};

const getSingleGatewayChartResultTemplate = (
    widgetType: WidgetType
): CreateChartState => {
    const columns: CreateChartState["columns"] = [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "gatewayName",
            name: "Gateway Name",
        },
        {
            key: "gatewayId",
            name: "UUID",
        },
    ];

    if (widgetType === WidgetType.GATEWAY_CARD_OVERVIEW) {
        columns.push({
            key: "showLocation",
            name: "Show Location",
            render: (data: FormGateway) => (
                <Form.Switch
                    id={`result-showLocation-${data.gatewayId}`}
                    checked={data.showLocation}
                    disabled
                    readOnly
                />
            ),
        });
    }

    return {
        title: "Creating New Widget(s)",
        data: [],
        columns,
        transform: (data: FormGateway, widgetType: WidgetType) => {
            const { source, chart } = getChartSourceFromWidgetType(widgetType);
            const attribute: {
                gateway_id: string;
                show_location?: boolean;
            } = {
                gateway_id: data.gatewayId,
            };

            // Only apply show_location if it exists in the form data, to avoid issues with older widgets that don't have this field
            if (typeof data.showLocation === "boolean") {
                attribute.show_location = !!data.showLocation;
            }

            return {
                attributes: [attribute],
                name: data.chartName,
                chart,
                source,
            };
        },
        validate() {
            return this.data.map((item) => ({
                isValid: CHART_NAME_PATTERN.test(item.chartName.trim()),
                message: CHART_ERROR_MESSAGE,
            }));
        },
    };
};

const CombinedGatewayChartResultTemplate: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "",
            name: "No. of Gateway",
            render: (data: any) => data.attributes?.length,
        },
    ],
    transform: (
        data: FormData & {
            deviceId: string;
            attributes?: { gatewayId: string }[];
        },
        widgetType: WidgetType
    ) => {
        const { source, chart } = getChartSourceFromWidgetType(widgetType);

        return {
            attributes: (data.attributes ?? []).map((d) => ({
                gateway_id: d.gatewayId,
            })),
            name: data.chartName,
            source,
            chart,
        };
    },
    validate() {
        return this.data.map((item) => ({
            isValid: CHART_NAME_PATTERN.test(item.chartName.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

export const updateFormData2 = (
    currentFormData: FormData,
    selectedGateways: Record<string, Registry>,
    widgetType?: WidgetType
): FormData => {
    // Update selected gateways, add or remove based on selection
    const newGatewayIds = Object.keys(selectedGateways);
    const gatewayFormData = currentFormData.gateways ?? [];
    const currentGatewayIds = gatewayFormData.map((g: any) => g.gatewayId);

    // Gateways to be added
    const toAdd = _.difference(newGatewayIds, currentGatewayIds);

    const newSelectedGateways = gatewayFormData.filter((gateway: any) =>
        newGatewayIds.includes(gateway.gatewayId)
    );

    for (const gatewayId of toAdd) {
        const registry = selectedGateways[gatewayId];
        const gatewayForm: FormGateway = {
            chartName: registry.name + " - Widget",
            gatewayName: registry.name,
            gatewayId,
            uuid: registry.uuid,
            deviceId: gatewayId,
        };

        if (widgetType === WidgetType.GATEWAY_CARD_OVERVIEW) {
            const hasLocation = !!(
                (
                    registry.location?.length === 2 &&
                    registry.location?.[0] &&
                    registry.location?.[1]
                ) // [0, 0] is considered as no location
            );
            gatewayForm.showLocation = false;
            gatewayForm.hasLocation = hasLocation;
        }

        newSelectedGateways.push(gatewayForm);
    }

    const updatedFormData = {
        ...currentFormData,
        gateways: newSelectedGateways,
    };

    return updatedFormData;
};

const DashboardCreateGatewayComponent = ({
    widget,
}: {
    widget: WidgetItem;
}) => {
    const widgetConfig = getWidgetConfigByWidgetType(widget.widgetType);
    const isCombined = widgetConfig.isCombined;
    const [showSelectModal, setShowSelectModal] = React.useState(false);
    const [selectedGateways, setSelectedGateways] = React.useState<
        Record<string, Registry>
    >({});

    const initialFormData: FormData = {};
    if (isCombined) {
        initialFormData.chartName = widgetConfig.defaultWidgetName ?? "";
    }
    const [formData, setFormData] = React.useState<FormData>(initialFormData);

    const { createModalShow, setCreateModalShow, createData } =
        useCreateDashboardStore();

    const resultTemplate = useMemo(() => {
        if (isCombined) {
            return CombinedGatewayChartResultTemplate;
        }

        return getSingleGatewayChartResultTemplate(widget.widgetType);
    }, [isCombined, widget.widgetType]);

    React.useEffect(() => {
        let createData;

        if (isCombined) {
            const gateways = formData?.gateways ?? [];

            createData = [
                {
                    ...formData,
                    attributes: gateways,
                    deviceId: gateways[0]?.deviceId, // Can use first device here because this is combined widget
                },
            ];
        } else {
            createData = formData?.gateways ?? [];
        }

        useCreateDashboardStore.setState({
            createData: {
                ...resultTemplate,
                data: createData,
            },
        });
    }, [isCombined, formData, resultTemplate]);

    const onGatewayChange = (event: any, data: Record<string, Registry>) => {
        useCreateDashboardStore.setState({
            createData: undefined,
        });

        const updatedFormData = updateFormData2(
            formData,
            data,
            widget.widgetType
        );

        setFormData(updatedFormData);
        setSelectedGateways(data);
    };

    const formChange = (e: ChangeEvent<HTMLFormElement>) => {
        const { name, value } = e.target;

        if (!name.startsWith("gateways.")) {
            _.set(formData, name, value);
            setFormData({ ...formData });
            return;
        }

        const updateGatewayId = name.split(".")[1];
        const fieldName = name.split(".")[2];
        const gateway = (formData.gateways ?? []).find(
            (g) => g.gatewayId === updateGatewayId
        );

        if (!gateway) {
            return;
        }

        (gateway as any)[fieldName] = value;
        setFormData({ ...formData });
    };

    const onRemove = (gateway: any) => {
        const data = _.omit(selectedGateways, gateway.gatewayId);
        onGatewayChange(null, data);
    };

    return (
        <React.Fragment>
            {widgetConfig.isCombined && (
                <Row className="mt-3">
                    <Col sm="12">
                        <Form.Group>
                            <Form.Label>Widget name</Form.Label>
                            <Form.Control
                                name="chartName"
                                type="text"
                                placeholder={
                                    widgetConfig.defaultWidgetName ?? ""
                                }
                                onChange={(e) => formChange(e as any)}
                                defaultValue={formData?.chartName ?? ""}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
            <Row className="mt-3">
                <Col sm="12">
                    <div className="d-flex align-items-center gap-3">
                        <div>
                            <Button
                                variant="primary"
                                onClick={() => setShowSelectModal(true)}
                            >
                                Select Gateways
                            </Button>
                        </div>

                        {!!widgetConfig.isCombined &&
                            !!widgetConfig.maxItems && (
                                <div style={{ fontSize: 14, color: "#828FAB" }}>
                                    (Max {widgetConfig.maxItems} Gateways)
                                </div>
                            )}
                    </div>

                    {!widgetConfig.isCombined && (
                        <div
                            style={{ fontSize: 13, color: "#828FAB" }}
                            className="mt-2"
                        >
                            This widget type supports bulk creation. Select
                            multiple gateways to create a separate widget for
                            each.
                        </div>
                    )}

                    <DashboardCreateSelectGatewayModal
                        show={showSelectModal}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedGateways}
                        onSave={onGatewayChange}
                    />
                </Col>
            </Row>

            <Row className="mt-4 cstm-table">
                <Col sm={12}>
                    <Form id="chart-edit-form" onChange={formChange}>
                        {!!Object.values(selectedGateways).length && (
                            <div className="table-head" key="head">
                                <Row className="no-checkbox">
                                    <Col>Gateway Name</Col>

                                    {[
                                        WidgetType.GATEWAY_TABLE_STATUS,
                                        WidgetType.GATEWAY_CARD_UPTIME_HISTORY,
                                    ].includes(widget.widgetType) ? null : (
                                        <Col>Widget Name</Col>
                                    )}

                                    <Col>UUID</Col>
                                    {widget.widgetType ===
                                        WidgetType.GATEWAY_CARD_OVERVIEW && (
                                            <Col>Show Location</Col>
                                        )}
                                    <Col md={{ span: 1 }}></Col>
                                </Row>
                            </div>
                        )}

                        {(formData.gateways || []).map((gateway) => {
                            const fieldName = `gateways.${gateway.gatewayId}.chartName`;
                            const defaultValue = gateway?.chartName ?? "";
                            return (
                                <div
                                    key={gateway.gatewayId}
                                    id={gateway.gatewayId}
                                    className="table-row gateway-chart-info"
                                >
                                    <div className="mobile-chart-info">
                                        <Row className="no-checkbox">
                                            <Col>
                                                <Row>
                                                    <div className="selected-gateway-header">
                                                        <p>
                                                            {
                                                                gateway.gatewayName
                                                            }
                                                        </p>
                                                        <CloseOutlined
                                                            className="cursor-pointer text-primary-red-1"
                                                            onClick={() =>
                                                                onRemove(
                                                                    gateway
                                                                )
                                                            }
                                                            onKeyDown={() =>
                                                                onRemove(
                                                                    gateway
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </Row>
                                                {[
                                                    WidgetType.GATEWAY_TABLE_STATUS,
                                                    WidgetType.GATEWAY_CARD_UPTIME_HISTORY,
                                                ].includes(
                                                    widget.widgetType
                                                ) ? null : (
                                                    <Row>
                                                        <div className="selected-gateway-info">
                                                            <Form.Group>
                                                                <Form.Label>
                                                                    Widget Name
                                                                </Form.Label>
                                                                <Form.Control
                                                                    name={
                                                                        fieldName
                                                                    }
                                                                    type="text"
                                                                    placeholder="Widget Name"
                                                                    defaultValue={
                                                                        defaultValue
                                                                    }
                                                                />
                                                            </Form.Group>
                                                        </div>
                                                    </Row>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="desktop-chart-info">
                                        <Row className="no-checkbox">
                                            <Col>{gateway.gatewayName}</Col>
                                            {[
                                                WidgetType.GATEWAY_TABLE_STATUS,
                                                WidgetType.GATEWAY_CARD_UPTIME_HISTORY,
                                            ].includes(
                                                widget.widgetType
                                            ) ? null : (
                                                <Col>
                                                    <Form.Control
                                                        name={fieldName}
                                                        type="text"
                                                        placeholder="Widget Name"
                                                        defaultValue={
                                                            defaultValue
                                                        }
                                                    />
                                                </Col>
                                            )}
                                            <Col>{gateway.gatewayId}</Col>

                                            {widget.widgetType ===
                                                WidgetType.GATEWAY_CARD_OVERVIEW && (
                                                    <Col className="d-flex align-items-center gap-3">
                                                        <div>
                                                            <Form.Switch
                                                                id={`showLocation-${gateway.gatewayId}`}
                                                                checked={
                                                                    gateway.showLocation
                                                                }
                                                                disabled={
                                                                    !gateway.hasLocation
                                                                }
                                                                onChange={(
                                                                    e: ChangeEvent<HTMLInputElement>
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    const updated =
                                                                        (
                                                                            formData.gateways ??
                                                                            []
                                                                        ).map((g) =>
                                                                            g.gatewayId ===
                                                                                gateway.gatewayId
                                                                                ? {
                                                                                    ...g,
                                                                                    showLocation:
                                                                                        e
                                                                                            .target
                                                                                            .checked,
                                                                                }
                                                                                : g
                                                                        );
                                                                    setFormData({
                                                                        ...formData,
                                                                        gateways:
                                                                            updated,
                                                                    });
                                                                }}
                                                            />
                                                        </div>
                                                        {!gateway.hasLocation && (
                                                            <span
                                                                style={{
                                                                    fontSize: 12,
                                                                    color: "#828FAB",
                                                                }}
                                                            >
                                                                Location information
                                                                not available
                                                            </span>
                                                        )}
                                                    </Col>
                                                )}

                                            <Col
                                                md={{ span: 1 }}
                                                className="d-flex justify-content-end"
                                            >
                                                <CloseOutlined
                                                    className="cursor-pointer mr-2 text-primary-red-1"
                                                    onClick={() =>
                                                        onRemove(gateway)
                                                    }
                                                    onKeyDown={() =>
                                                        onRemove(gateway)
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            );
                        })}
                    </Form>
                </Col>
            </Row>
            <CreateChartModal
                show={createModalShow && !_.isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(DashboardCreateGatewayComponent);
